import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Spinner,
  Alert,
  Button,
  Table,
  Tabs,
  Tab,
  Form,
  Row,
  Col,
  Pagination,
  Badge
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTimes,
  faFileAlt,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import api from '@services/api'; // Assurez-vous que le chemin est correct
import { toast } from 'react-toastify';
import Moment from 'moment'; // Pour le formatage des dates

// Hook pour le debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

const SalonLogsModal = ({ show, onHide, salonId, salonName }) => {
  const [activeTab, setActiveTab] = useState('actions');
  const [actionLogsData, setActionLogsData] = useState({
    data: [],
    total: 0,
    current_page: 1,
    last_page: 1
  });
  const [emailLogsData, setEmailLogsData] = useState({
    data: [],
    total: 0,
    current_page: 1,
    last_page: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [perPage] = useState(10); // Nombre d'éléments par page

  const [actionSearch, setActionSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  const debouncedActionSearch = useDebounce(actionSearch, 500);
  const debouncedEmailSearch = useDebounce(emailSearch, 500);

  const fetchLogs = useCallback(
    async (page = 1, type = activeTab, search = '') => {
      if (!salonId) return;
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (type === 'actions') {
        params.append('action_page', page);
        if (search) params.append('search_action', search);
      } else if (type === 'emails') {
        params.append('email_page', page);
        if (search) params.append('search_email', search);
      }
      // L'API doit pouvoir gérer les deux types de recherche et de pagination simultanément
      // ou alors il faut s'assurer que les paramètres non pertinents sont vides.
      // Pour l'instant, on envoie les deux types de recherche si elles sont définies.
      if (debouncedActionSearch && type !== 'actions')
        params.append('search_action', debouncedActionSearch);
      if (debouncedEmailSearch && type !== 'emails')
        params.append('search_email', debouncedEmailSearch);

      params.append('per_page', perPage);

      try {
        const response = await api.get(
          `/admin/partner-salons/${salonId}/logs?${params.toString()}`
        );
        if (response.data.success) {
          setActionLogsData(
            response.data.data.action_logs || {
              data: [],
              total: 0,
              current_page: 1,
              last_page: 1
            }
          );
          setEmailLogsData(
            response.data.data.email_logs || {
              data: [],
              total: 0,
              current_page: 1,
              last_page: 1
            }
          );
        } else {
          const errorMessage =
            response.data.message ||
            `Erreur lors du chargement des logs de type ${type}`;
          setError(errorMessage);
          toast.error(errorMessage);
        }
      } catch (err) {
        console.error(`Error fetching salon ${type} logs:`, err);
        const errorMessage = `Une erreur est survenue lors du chargement des logs de type ${type}.`;
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [salonId, perPage, activeTab, debouncedActionSearch, debouncedEmailSearch] // debounced searches sont des dépendances
  );

  useEffect(() => {
    if (show && salonId) {
      // Lorsque la modale s'ouvre ou que l'onglet actif change, ou que la recherche change
      if (activeTab === 'actions') {
        fetchLogs(1, 'actions', debouncedActionSearch); // Toujours à la page 1 si la recherche change
      } else if (activeTab === 'emails') {
        fetchLogs(1, 'emails', debouncedEmailSearch); // Toujours à la page 1 si la recherche change
      }
    }
  }, [
    show,
    salonId,
    activeTab,
    debouncedActionSearch,
    debouncedEmailSearch,
    fetchLogs
  ]);
  // fetchLogs est maintenant dans les dépendances, mais il est protégé par useCallback

  const handlePageChange = (pageNumber, type) => {
    if (type === 'actions') {
      fetchLogs(pageNumber, 'actions', debouncedActionSearch);
    } else if (type === 'emails') {
      fetchLogs(pageNumber, 'emails', debouncedEmailSearch);
    }
  };

  const handleSelectTab = k => {
    setActiveTab(k);
    // Le useEffect ci-dessus s'occupera de fetch les logs pour le nouvel onglet
    // en repartant de la page 1.
  };

  const renderPagination = (logData, type) => {
    if (!logData || logData.last_page <= 1) return null;
    const items = [];
    for (let number = 1; number <= logData.last_page; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === logData.current_page}
          onClick={() => handlePageChange(number, type)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return <Pagination className="justify-content-center">{items}</Pagination>;
  };

  const formatLogDate = dateString => {
    return Moment(dateString).format('DD/MM/YYYY HH:mm:ss');
  };

  const getEmailStatusBadge = status => {
    switch (status) {
      case 'sent':
      case 'queued':
        return <Badge bg="success">{status}</Badge>;
      case 'failed':
        return <Badge bg="danger">{status}</Badge>;
      case 'pending':
        return <Badge bg="warning">{status}</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const renderActionLogs = () => {
    if (loading && activeTab === 'actions')
      return (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      );
    if (error && activeTab === 'actions')
      return <Alert variant="danger">{error}</Alert>;
    if (!actionLogsData.data || actionLogsData.data.length === 0) {
      return <Alert variant="info">Aucun log d'action trouvé.</Alert>;
    }
    return (
      <>
        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Utilisateur</th>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {actionLogsData.data.map(log => (
              <tr key={log.id}>
                <td>{formatLogDate(log.created_at)}</td>
                <td>
                  {log.user
                    ? `${log.user.firstname} ${log.user.lastname}`
                    : 'Système'}
                </td>
                <td>{log.action}</td>
                <td>{log.description}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {renderPagination(actionLogsData, 'actions')}
      </>
    );
  };

  const renderEmailLogs = () => {
    if (loading && activeTab === 'emails')
      return (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      );
    if (error && activeTab === 'emails')
      return <Alert variant="danger">{error}</Alert>;
    if (!emailLogsData.data || emailLogsData.data.length === 0) {
      return <Alert variant="info">Aucun log d'email trouvé.</Alert>;
    }
    return (
      <>
        <Table striped bordered hover responsive size="sm">
          <thead>
            <tr>
              <th>Date Envoi</th>
              <th>Destinataire</th>
              <th>Email</th>
              <th>Sujet</th>
              <th>Type</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {emailLogsData.data.map(log => (
              <tr key={log.id}>
                <td>{formatLogDate(log.sent_at)}</td>
                <td>{log.recipient_name}</td>
                <td>{log.recipient_email}</td>
                <td>{log.subject}</td>
                <td>{log.email_type}</td>
                <td>{getEmailStatusBadge(log.status)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {renderPagination(emailLogsData, 'emails')}
      </>
    );
  };

  const searchBar = useMemo(() => {
    const currentSearch = activeTab === 'actions' ? actionSearch : emailSearch;
    const setCurrentSearch =
      activeTab === 'actions' ? setActionSearch : setEmailSearch;

    return (
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Col sm={12}>
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faSearch} />
            </span>
            <Form.Control
              type="text"
              placeholder={`Rechercher dans les logs ${
                activeTab === 'actions' ? "d'actions" : "d'emails"
              }...`}
              value={currentSearch}
              onChange={e => setCurrentSearch(e.target.value)}
            />
            {currentSearch && (
              <Button
                variant="outline-secondary"
                onClick={() => setCurrentSearch('')}
                className="border-start-0"
                style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            )}
          </div>
        </Col>
      </Form.Group>
    );
  }, [activeTab, actionSearch, emailSearch, setActionSearch, setEmailSearch]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Logs du salon : {salonName || 'Chargement...'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ minHeight: '50vh', maxHeight: '70vh', overflowY: 'auto' }}
      >
        {searchBar}
        <Tabs
          id="salon-logs-tabs"
          activeKey={activeTab}
          onSelect={handleSelectTab}
          className="mb-3"
        >
          <Tab
            eventKey="actions"
            title={
              <>
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Actions{' '}
                <Badge pill bg="secondary">
                  {actionLogsData.total}
                </Badge>
              </>
            }
          >
            {activeTab === 'actions' && renderActionLogs()}
          </Tab>
          <Tab
            eventKey="emails"
            title={
              <>
                <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                Emails{' '}
                <Badge pill bg="secondary">
                  {emailLogsData.total}
                </Badge>
              </>
            }
          >
            {activeTab === 'emails' && renderEmailLogs()}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SalonLogsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  salonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  salonName: PropTypes.string
};

SalonLogsModal.defaultProps = {
  salonName: '',
  salonId: null
};

export default SalonLogsModal;
